import { useEffect, useState, useCallback } from "react";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "epersonel.cookie-consent.v1";

const defaultPrefs = { necessary: true, analytics: false, marketing: false };

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Show slightly delayed to avoid layout jank on first paint
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      } else {
        setPrefs({ ...defaultPrefs, ...JSON.parse(saved) });
      }
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) setPrefs({ ...defaultPrefs, ...JSON.parse(saved) });
      } catch {
        /* noop */
      }
      setManage(true);
      setOpen(true);
    };
    window.addEventListener("epersonel:open-cookie-settings", handler);
    return () =>
      window.removeEventListener("epersonel:open-cookie-settings", handler);
  }, []);

  const persist = useCallback((value) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    setPrefs(value);
    setOpen(false);
    setManage(false);
  }, []);

  const acceptAll = () =>
    persist({ necessary: true, analytics: true, marketing: true });
  const onlyNecessary = () =>
    persist({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => persist({ ...prefs, necessary: true });

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 p-4 sm:p-6 pointer-events-none"
      data-testid="cookie-consent"
      role="dialog"
      aria-labelledby="cookie-title"
    >
      <div className="ep-glass max-w-3xl mx-auto pointer-events-auto p-5 sm:p-6 rounded-2xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full grid place-items-center bg-[#00F22E]/10 text-[#00F22E]">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="cookie-title"
              className="font-display font-semibold text-white text-lg"
            >
              Çerez Tercihleri
            </h2>
            <p className="text-sm text-muted mt-1 leading-relaxed">
              Web sitemizin çalışması için zorunlu çerezleri kullanıyoruz. Analitik
              ve pazarlama çerezleri yalnızca onayınızla etkinleştirilir.
            </p>

            {manage && (
              <div className="mt-4 space-y-3">
                <PrefRow
                  title="Zorunlu Çerezler"
                  desc="Sitenin temel işlevleri için gereklidir. Devre dışı bırakılamaz."
                  checked={true}
                  disabled
                />
                <PrefRow
                  title="Analitik Çerezler"
                  desc="Site kullanımını anlamamıza yardımcı olur."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testId="cookie-analytics-toggle"
                />
                <PrefRow
                  title="Pazarlama Çerezleri"
                  desc="Reklam etkinliğini ölçmek için kullanılabilir."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  testId="cookie-marketing-toggle"
                />
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              {!manage && (
                <>
                  <button
                    className="btn-primary text-sm"
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                  >
                    Tümünü Kabul Et
                  </button>
                  <button
                    className="btn-secondary text-sm"
                    onClick={onlyNecessary}
                    data-testid="cookie-only-necessary"
                  >
                    Yalnızca Zorunlu
                  </button>
                  <button
                    className="btn-secondary text-sm"
                    onClick={() => setManage(true)}
                    data-testid="cookie-manage"
                  >
                    Tercihleri Yönet
                  </button>
                </>
              )}
              {manage && (
                <button
                  className="btn-primary text-sm"
                  onClick={savePrefs}
                  data-testid="cookie-save-preferences"
                >
                  Tercihleri Kaydet
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
      <div>
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="text-xs text-muted mt-0.5">{desc}</div>
      </div>
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 accent-[#00F22E]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testId}
      />
    </label>
  );
}
